import React, { useState, useEffect, useCallback, useRef } from "react";
import { BsMusicNoteList } from "react-icons/bs";
import {
  FaMusic,
  FaChevronLeft,
  FaChevronRight,
  FaTimes,
} from "react-icons/fa";

const importAll = (r) => r.keys().map(r);

// Tutte le locandine della cartella, in ordine di nome file
const locandine = importAll(
  require.context("../imgs/Locandine", false, /\.(png|jpe?g|webp)$/),
).sort((a, b) => String(a).localeCompare(String(b)));

/* ------------------------------------------------------------------ */
/*  Carousel                                                           */
/*  - scorre da solo ogni 4 secondi, senza fine (torna all'inizio)      */
/*  - la locandina al centro è grande, le altre più piccole e sfumate   */
/*  - si ferma quando ci passi sopra con il mouse o è aperta la         */
/*    locandina a schermo intero                                       */
/*  - frecce, puntini e scorrimento con il dito su mobile               */
/*  - click sulla locandina centrale = ingrandisci, sulle altre = vai   */
/* ------------------------------------------------------------------ */
const Carousel = ({ images, index, setIndex, paused, onOpen }) => {
  const total = images.length;
  const [hover, setHover] = useState(false);
  const touchStartX = useRef(null);

  useEffect(() => {
    if (hover || paused || total <= 1) return;
    const id = setInterval(() => setIndex((i) => (i + 1) % total), 2500);
    return () => clearInterval(id);
  }, [hover, paused, total, index, setIndex]);

  const prev = () => setIndex((i) => (i - 1 + total) % total);
  const next = () => setIndex((i) => (i + 1) % total);

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const diff = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(diff) > 40) {
      if (diff < 0) next();
      else prev();
    }
  };

  return (
    <div
      className="relative w-full max-w-5xl rounded-3xl bg-gradient-to-br from-white/70 via-amber-50 to-amber-100/80 border border-amber-100 shadow-md overflow-hidden"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* Palco */}
      <div className="relative h-[340px] sm:h-[410px] md:h-[470px]">
        {images.map((src, i) => {
          // Distanza (con segno) dalla locandina centrale, in cerchio
          const raw = (i - index + total) % total;
          const d = raw > total / 2 ? raw - total : raw;
          const abs = Math.abs(d);
          const dc = Math.max(-3, Math.min(3, d));
          const isCenter = d === 0;

          const opacity = abs === 0 ? 1 : abs === 1 ? 0.75 : abs === 2 ? 0.35 : 0;

          return (
            <button
              key={src}
              type="button"
              onClick={() => (isCenter ? onOpen() : setIndex(i))}
              aria-label={
                isCenter
                  ? `Ingrandisci la locandina ${i + 1}`
                  : `Vai alla locandina ${i + 1}`
              }
              tabIndex={abs > 1 ? -1 : 0}
              className={`absolute left-1/2 top-1/2 w-[190px] h-[280px] sm:w-[240px] sm:h-[350px] md:w-[280px] md:h-[400px] rounded-2xl overflow-hidden bg-amber-100 transition-all duration-700 ease-out motion-reduce:transition-none focus:outline-none focus-visible:ring-4 focus-visible:ring-red-400 ${
                isCenter
                  ? "shadow-2xl ring-4 ring-white cursor-zoom-in"
                  : "shadow-lg cursor-pointer"
              } ${abs > 2 ? "pointer-events-none" : ""}`}
              style={{
                transform: `translate(-50%, -50%) translateX(${
                  dc * 58
                }%) scale(${1 - 0.16 * Math.min(abs, 3)})`,
                opacity,
                zIndex: 10 - abs,
              }}
            >
              <img
                src={src}
                alt=""
                aria-hidden="true"
                className="absolute inset-0 w-full h-full object-cover scale-110 blur-xl opacity-60"
              />
              <img
                src={src}
                alt={`Locandina ${i + 1}`}
                className="relative w-full h-full object-contain"
              />
            </button>
          );
        })}
      </div>

      {total > 1 && (
        <>
          <button
            type="button"
            onClick={prev}
            aria-label="Locandina precedente"
            className="absolute left-3 top-1/2 -translate-y-1/2 z-30 flex items-center justify-center w-11 h-11 rounded-full bg-black/50 text-white hover:bg-red-600 transition-colors focus:outline-none focus-visible:ring-4 focus-visible:ring-red-400"
          >
            <FaChevronLeft />
          </button>
          <button
            type="button"
            onClick={next}
            aria-label="Locandina successiva"
            className="absolute right-3 top-1/2 -translate-y-1/2 z-30 flex items-center justify-center w-11 h-11 rounded-full bg-black/50 text-white hover:bg-red-600 transition-colors focus:outline-none focus-visible:ring-4 focus-visible:ring-red-400"
          >
            <FaChevronRight />
          </button>

          {/* Puntini (o contatore se le locandine sono tante) */}
          <div className="pb-5 flex justify-center">
            {total <= 10 ? (
              <div className="flex gap-2">
                {images.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setIndex(i)}
                    aria-label={`Vai alla locandina ${i + 1}`}
                    className={`h-2.5 rounded-full transition-all duration-300 ${
                      i === index
                        ? "w-7 bg-red-600"
                        : "w-2.5 bg-red-600/30 hover:bg-red-600/60"
                    }`}
                  />
                ))}
              </div>
            ) : (
              <span className="bg-black/60 text-white text-xs font-semibold px-3 py-1 rounded-full">
                {index + 1} / {total}
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Lightbox                                                           */
/*  Locandina a schermo intero. Si sfoglia con le frecce (o con i tasti */
/*  freccia) e si chiude con la X, con un click fuori o con Esc.       */
/* ------------------------------------------------------------------ */
const Lightbox = ({ images, index, onChange, onClose }) => {
  const total = images.length;

  const prev = useCallback(
    () => onChange((index - 1 + total) % total),
    [index, total, onChange],
  );
  const next = useCallback(
    () => onChange((index + 1) % total),
    [index, total, onChange],
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    document.addEventListener("keydown", onKey);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [prev, next, onClose]);

  return (
    <div
      className="fixed inset-0 bg-black/85 flex items-center justify-center z-50 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Locandina ingrandita"
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Chiudi"
        className="absolute top-4 right-4 text-white text-3xl p-2 hover:text-red-400 transition-colors"
      >
        <FaTimes />
      </button>

      {total > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              prev();
            }}
            aria-label="Locandina precedente"
            className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 flex items-center justify-center w-11 h-11 rounded-full bg-white/15 text-white hover:bg-red-600 transition-colors"
          >
            <FaChevronLeft />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              next();
            }}
            aria-label="Locandina successiva"
            className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 flex items-center justify-center w-11 h-11 rounded-full bg-white/15 text-white hover:bg-red-600 transition-colors"
          >
            <FaChevronRight />
          </button>
          <span className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 text-white text-sm font-semibold px-4 py-1 rounded-full">
            {index + 1} / {total}
          </span>
        </>
      )}

      <img
        src={images[index]}
        alt="Locandina ingrandita"
        onClick={(e) => e.stopPropagation()}
        className="max-w-[80vw] max-h-[85vh] rounded-xl shadow-2xl"
      />
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Pagina                                                             */
/* ------------------------------------------------------------------ */
const Concerti = () => {
  const [index, setIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const closeLightbox = useCallback(() => setLightboxOpen(false), []);

  return (
    <div className="flex flex-col items-center w-full min-h-screen bg-gradient-to-r from-white to-amber-50 text-gray-800 overflow-hidden">
      <BsMusicNoteList className="fixed text-amber-200/40 text-[180px] rotate-12 -top-5 -right-5 z-0" />

      {/* --- PROSSIMI CONCERTI --- */}
      <section className="relative z-10 w-full bg-red-700 text-white py-14 px-6 text-center">
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-wide text-amber-200">
          Prossimi concerti
        </h1>
        <FaMusic className="mx-auto mt-8 text-5xl text-amber-200 animate-pulse" />
        <p className="mt-4 text-4xl md:text-5xl font-extrabold">Coming Soon</p>
        <p className="mt-2 text-lg md:text-xl text-amber-100">
          Nuovi concerti in arrivo
        </p>
      </section>

      {/* --- I NOSTRI CONCERTI --- */}
      {locandine.length > 0 && (
        <section className="relative z-10 w-11/12 max-w-5xl my-14 flex flex-col items-center">
          <h2 className="text-3xl md:text-4xl font-bold text-red-600 mb-8 self-start">
            I nostri concerti
          </h2>
          <Carousel
            images={locandine}
            index={index}
            setIndex={setIndex}
            paused={lightboxOpen}
            onOpen={() => setLightboxOpen(true)}
          />
        </section>
      )}

      {lightboxOpen && (
        <Lightbox
          images={locandine}
          index={index}
          onChange={setIndex}
          onClose={closeLightbox}
        />
      )}
    </div>
  );
};

export default Concerti;